import React, { useState, useEffect, useCallback, memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  motion,
  MotionConfig,
  AnimatePresence,
  useScroll,
  useTransform,
  useSpring,
  useReducedMotion,
} from 'framer-motion';
import Logo from '../logo/FRIENDS HOTEL LOGO.png';
import './Navbar.css';

const VERSION = '0.1.8.2';

const NAV_LINKS = [
  { url: '/',                          label: 'Home'        },
  { url: '/Hotel/Gangtok-East-Inn',    label: 'Gangtok'     },
  { url: '/Rinchenpong',               label: 'Rinchenpong' },
  { url: '/Hotel/Norbulingka-Retreat', label: 'Pelling'     },
  { url: '/tour',                      label: 'Tour'        },
  { url: '/blog',                      label: 'Blog'        },
];

const isActive = (url, pathname) =>
  url === '/' ? pathname === '/' : pathname.startsWith(url);

/* ─── Motion variants ────────────────────────────────────────────
   Drawer: spring slide from right.
   Overlay: quick opacity fade.
   Link list: staggered spring slide-in per item.
   Active dot: layoutId shared spring — slides between nav items.
   ─────────────────────────────────────────────────────────────── */

const drawerVariants = {
  hidden:  { x: '100%' },
  visible: { x: 0,      transition: { type: 'spring', stiffness: 340, damping: 38 } },
  exit:    { x: '100%', transition: { type: 'spring', stiffness: 400, damping: 42 } },
};

const overlayVariants = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.22 } },
  exit:    { opacity: 0, transition: { duration: 0.18 } },
};

const linkListVariants = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.055, delayChildren: 0.08 } },
};

const linkItemVariants = {
  hidden:  { x: 24, opacity: 0 },
  visible: {
    x: 0, opacity: 1,
    transition: { type: 'spring', stiffness: 300, damping: 26 },
  },
};

/* ─── Component ──────────────────────────────────────────────── */

const Navbar = memo(() => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const prefersReduced = useReducedMotion();

  /* ── Scroll-driven glass morph ──────────────────────────────
     scrollY → spring-smoothed → interpolated CSS values.
     Spring config: high stiffness + low mass = responsive but
     physically smooth (avoids raw scroll jitter).
     Reduced-motion: stiffness=9999 makes spring instantaneous. */
  const { scrollY } = useScroll();
  const smoothY = useSpring(scrollY,
    prefersReduced
      ? { stiffness: 9999, damping: 9999, restDelta: 0.001 }
      : { stiffness: 260,  damping: 28,   mass: 0.3, restDelta: 0.5 }
  );

  /* Morph range: fully transparent at 0 px → full glass at 60 px */
  const MORPH = [0, 60];

  /* Opaque-enough white instead of a blurred backdrop. A scroll-driven
     backdrop-filter re-runs a full-viewport blur readback on every
     spring tick, which is the most expensive thing on the page for a
     low-memory phone. Painting a near-solid background costs nothing
     and reads better against photography underneath. */
  const headerBg = useTransform(smoothY, MORPH, [
    'rgba(255,255,255,0.00)',
    'rgba(255,255,255,0.96)',
  ]);
  const headerBorder = useTransform(smoothY, MORPH, [
    'rgba(218,165,32,0.00)',
    'rgba(218,165,32,0.28)',
  ]);
  const headerShadow = useTransform(smoothY, MORPH, [
    '0 1px 0 rgba(218,165,32,0.00), 0 6px 24px rgba(0,0,0,0.00)',
    '0 1px 0 rgba(218,165,32,0.14), 0 6px 24px rgba(0,0,0,0.10)',
  ]);

  /* Binary "at-top" flag → drives data-scrolled attr for CSS colour tokens */
  useEffect(() => scrollY.onChange(v => setScrolled(v > 20)), [scrollY]);

  /* Close drawer on route change */
  useEffect(() => { setMenuOpen(false); }, [pathname]);

  /* Lock body scroll while drawer is open */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const close  = useCallback(() => setMenuOpen(false), []);
  const toggle = useCallback(() => setMenuOpen(o => !o), []);

  return (
    /* MotionConfig: propagates reducedMotion="user" to ALL child
       motion components — drawer, overlay, stagger, layoutId dot. */
    <MotionConfig reducedMotion="user">
      <>
        {/* ── Fixed shell: single position:fixed ancestor for bar + header ── */}
        <div className="nb-shell">
        {/* ── Shimmer announcement bar (scrolls with page) ────── */}
        <div className="nb-announcement" aria-label="Promotional announcement">
          Himalayan escapes await —&nbsp;
          <a href="tel:+916289428163">call +91&nbsp;6289428163</a>
          &nbsp;to book
        </div>

        {/* ── Header ───────────────────────────────────────────────
             Animated visual properties are injected as MotionValues;
             CSS carries structural/layout properties only. */}
        <motion.header
          className="nb-root"
          data-scrolled={scrolled}
          style={{
            background:        headerBg,
            borderBottomColor: headerBorder,
            boxShadow:         headerShadow,
          }}
        >
          <div className="nb-inner">

            {/* Logo */}
            <Link to="/" className="nb-logo" aria-label="Friends Hotel home">
              <img src={Logo} alt="Friends Hotel" className="nb-logo-img" />
              <div className="nb-logo-text">
                <span className="nb-logo-name">Friends Hotel</span>
                <span className="nb-logo-sub">Sikkim · ভারত</span>
              </div>
            </Link>

            {/* Desktop nav */}
            <ul className="nb-links" role="list">
              {NAV_LINKS.map(({ url, label }) => (
                <li key={url}>
                  <Link
                    to={url}
                    className={`nb-link${isActive(url, pathname) ? ' nb-active' : ''}`}
                  >
                    {label}
                    {/* layoutId="nb-active-dot" — framer-motion tracks this
                        span across re-renders and smoothly slides it between
                        whichever link is currently active.                   */}
                    {isActive(url, pathname) && (
                      <motion.span
                        className="nb-active-indicator"
                        layoutId="nb-active-dot"
                        transition={{ type: 'spring', stiffness: 380, damping: 38 }}
                      />
                    )}
                  </Link>
                </li>
              ))}

              <li aria-hidden="true"><span className="nb-divider" /></li>

              <li>
                <a href="tel:+916289428163" className="nb-cta">
                  <span className="nb-cta-icon" aria-hidden="true">✦</span>
                  Book Now
                </a>
              </li>
            </ul>

            {/* Hamburger */}
            <button
              className={`nb-hamburger${menuOpen ? ' nb-open' : ''}`}
              onClick={toggle}
              aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
              aria-expanded={menuOpen}
              aria-controls="nb-mobile-nav"
            >
              <span /><span /><span />
            </button>
          </div>
        </motion.header>
        </div>{/* /nb-shell */}

        {/* ── Overlay + Drawer ─────────────────────────────────────
             AnimatePresence: enables exit animations when menuOpen
             flips to false (components unmount after exit anim).    */}
        <AnimatePresence>
          {menuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                key="nb-overlay"
                className="nb-overlay"
                variants={overlayVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onClick={close}
                aria-hidden="true"
              />

              {/* Slide-in drawer */}
              <motion.nav
                key="nb-mobile-nav"
                id="nb-mobile-nav"
                className="nb-mobile-menu"
                variants={drawerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                aria-label="Mobile navigation"
              >
                {/* Drawer header */}
                <div className="nb-mobile-header">
                  <span className="nb-mobile-brand">
                    Friends <span>Hotel</span>
                  </span>
                  <button
                    className="nb-mobile-close"
                    onClick={close}
                    aria-label="Close menu"
                  >
                    {/* SVG X — no emoji/unicode char */}
                    <svg
                      viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                      width="16" height="16" aria-hidden="true"
                    >
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Staggered links — linkListVariants drives delayChildren
                    so each motion.div child receives linkItemVariants       */}
                <motion.div
                  className="nb-mobile-links"
                  variants={linkListVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {NAV_LINKS.map(({ url, label }) => (
                    <motion.div key={url} variants={linkItemVariants}>
                      <Link
                        to={url}
                        className={`nb-mobile-link${isActive(url, pathname) ? ' nb-active' : ''}`}
                        onClick={close}
                      >
                        {label}
                        {/* Chevron arrow (SVG, not text char) */}
                        <svg
                          className="nb-mobile-link-arrow"
                          viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                          width="14" height="14" aria-hidden="true"
                        >
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      </Link>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Drawer footer */}
                <div className="nb-mobile-footer">
                  <a href="tel:+916289428163" className="nb-mobile-cta">
                    ✦&nbsp;&nbsp;Book Now — +91&nbsp;6289428163
                  </a>
                  <p className="nb-mobile-meta">
                    Friends Hotel&nbsp;·&nbsp;Sikkim, ভারত&nbsp;·&nbsp;v{VERSION}
                  </p>
                </div>
              </motion.nav>
            </>
          )}
        </AnimatePresence>
      </>
    </MotionConfig>
  );
});

export default Navbar;
